import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatGBP, apiError } from "@/lib/api";
import { setUnsaved, confirmLeave } from "@/lib/unsaved";
import DocumentUpload from "@/components/DocumentUpload";
import DuplicateDialog, { conflictFrom } from "@/components/DuplicateDialog";
import ConfirmDetails from "@/components/ConfirmDetails";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Save, Send, AlertTriangle, Sparkles } from "lucide-react";
import PaymentChoice, { usePaymentDefaults, paymentPayload, paidAmount } from "@/components/PaymentChoice";
import { useFeatures } from "@/lib/features";

export default function RecordPurchasePage() {
  const { selectedStore, activeStoreId, stores } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  useEffect(() => { document.querySelector("[data-testid=main-scroll]")?.scrollTo(0, 0); }, [step]);
  const [documents, setDocuments] = useState([]);
  const [ocrData, setOcrData] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newSupplier, setNewSupplier] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [createdId, setCreatedId] = useState(null);

  // If "Both Stores" is selected, employee must pick a specific store
  const effectiveStoreId = activeStoreId || (stores.length > 0 ? stores[0].id : null);

  const [form, setForm] = useState({
    store_id: effectiveStoreId || "",
    supplier_id: "",
    supplier_name: "",
    invoice_number: "",
    invoice_date: "",
    subtotal: "",
    vat_amount: "",
    total_amount: "",
    product_details: "",
    payment_status: "unpaid",
    payment_method: "",
    amount_paid: "",
    is_credit_note: false,
    notes: "",
  });

  useEffect(() => {
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (effectiveStoreId && !form.store_id) {
      setForm((p) => ({ ...p, store_id: effectiveStoreId }));
    }
  }, [effectiveStoreId]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const { phase2 } = useFeatures();
  usePaymentDefaults(form, setForm, true);

  const handleOcr = (data) => {
    setOcrData(data);
    if (data && !data.error) {
      setForm((p) => ({
        ...p,
        supplier_name: data.supplier_name || p.supplier_name,
        invoice_number: data.invoice_number || p.invoice_number,
        invoice_date: data.invoice_date || p.invoice_date,
        subtotal: data.subtotal ?? p.subtotal,
        vat_amount: data.vat_amount ?? p.vat_amount,
        total_amount: data.total_amount ?? p.total_amount,
        product_details: data.line_items
          ? data.line_items.map((i) => `${i.description} x${i.quantity} — £${i.total}`).join("\n")
          : p.product_details,
      }));
      if (data.supplier_name) {
        const match = suppliers.find((s) => s.name.toLowerCase().includes(data.supplier_name.toLowerCase()));
        if (match) {
          setForm((p) => ({ ...p, supplier_id: String(match.id), supplier_name: "" }));
          setNewSupplier(false);
        } else {
          setNewSupplier(true);
        }
      }
    }
  };

  const save = async (submit = false, extra = {}) => {
    const sid = form.store_id || effectiveStoreId;
    if (!sid) return toast.error("Please select a store first");
    setSaving(true);
    try {
      const payload = {
        store_id: parseInt(sid),
        supplier_id: newSupplier ? null : (form.supplier_id ? parseInt(form.supplier_id) : null),
        supplier_name: newSupplier ? form.supplier_name : null,
        invoice_number: form.invoice_number,
        invoice_date: form.invoice_date,
        subtotal: parseFloat(form.subtotal) || 0,
        vat_amount: parseFloat(form.vat_amount) || 0,
        total_amount: parseFloat(form.total_amount) || 0,
        product_details: form.product_details,
        ...paymentPayload(form, phase2),
        is_credit_note: form.is_credit_note,
        document_ids: documents.map((d) => d.id),
        notes: form.notes,
        ...extra,
      };
      const res = createdId ? await api.put(`/purchases/${createdId}`, payload) : await api.post("/purchases", payload);
      setCreatedId(res.data.id);
      setConflict(null);
      if (submit && !extra.duplicate_review_reason) {
        const r = await api.patch(`/purchases/${res.data.id}/submit`, { confirmed, acknowledge_cross_store: !!extra.acknowledge_cross_store, paid_amount: paidAmount(form) });
        toast.success(r.data.message || "Purchase submitted");
      } else {
        toast.success(extra.duplicate_review_reason ? "Draft saved — waiting for a manager to review the duplicate" : "Draft saved");
      }
      setUnsaved(false);
      navigate("/submissions");
    } catch (err) {
      const c = conflictFrom(err);
      if (c) setConflict({ ...c, submit, extra });
      else toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const steps = ["Upload Invoice", "Invoice Details", "Payment & Submit"];
  const dirty = documents.length > 0 || !!(form.invoice_number || form.supplier_id || form.supplier_name || form.total_amount || form.subtotal);
  useEffect(() => { setUnsaved(dirty); }, [dirty]);
  useEffect(() => () => { setUnsaved(false); }, []);

  return (
    <div className="space-y-5" data-testid="record-purchase-page">
      <div className="flex items-center gap-3">
        <button onClick={() => { if (confirmLeave()) navigate(-1); }} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-navy/40 hover:text-navy" aria-label="Back"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="text-xl font-heading font-bold text-navy">Record Purchase</h1>
      </div>

      {/* Store picker when "Both Stores" is active */}
      {!activeStoreId && stores.length > 1 && (
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-navy">Store</Label>
          <Select value={String(form.store_id)} onValueChange={(v) => set("store_id", v)}>
            <SelectTrigger className="gm-input"><SelectValue placeholder="Select store" /></SelectTrigger>
            <SelectContent>
              {stores.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <StepIndicator steps={steps} current={step} />

      {step === 1 && (
        <div className="space-y-4">
          <p className="text-sm text-navy/50">Upload the supplier invoice or receipt</p>
          <DocumentUpload kind="invoice" storeId={parseInt(form.store_id || effectiveStoreId)} onUpload={(doc) => setDocuments((p) => [...p, doc])} onOcrResult={handleOcr} onRemove={(did) => setDocuments((p) => p.filter((x) => x.id !== did))} />
          {ocrData && !ocrData.error && (
            <div className="gm-ocr-banner"><Sparkles className="w-4 h-4 text-gold shrink-0" /><span>AI extracted data — verify on the next step.</span></div>
          )}
          <div className="flex gap-3 pt-2">
            <button onClick={() => setStep(2)} className="flex-1 gm-btn-gold flex items-center justify-center gap-2" data-testid="step1-next-btn">Next <ArrowRight className="w-4 h-4" /></button>
          </div>
          <button onClick={() => save(false)} disabled={saving} className="w-full text-sm text-navy/40 hover:text-navy py-2 min-h-[44px]" data-testid="save-draft-btn">
            <Save className="w-3.5 h-3.5 inline mr-1" /> Save as draft
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          {ocrData && !ocrData.error && (
            <div className="gm-ocr-banner"><AlertTriangle className="w-4 h-4 text-gold shrink-0" /><span>Extracted by AI — please verify all fields before continuing</span></div>
          )}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-navy">Supplier</Label>
            {!newSupplier ? (
              <div className="flex gap-2">
                <Select value={form.supplier_id} onValueChange={(v) => set("supplier_id", v)}>
                  <SelectTrigger className="gm-input flex-1" data-testid="supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
                <button type="button" onClick={() => setNewSupplier(true)} className="text-xs text-gold hover:text-gold-light whitespace-nowrap px-2 min-h-[44px]" data-testid="new-supplier-btn">+ New</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input value={form.supplier_name} onChange={(e) => set("supplier_name", e.target.value)} placeholder="New supplier name" className="gm-input flex-1" data-testid="new-supplier-input" />
                <button type="button" onClick={() => setNewSupplier(false)} className="text-xs text-navy/40 px-2">Cancel</button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Invoice No."><Input value={form.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} className="gm-input" data-testid="invoice-number-input" /></Field>
            <Field label="Invoice Date"><Input value={form.invoice_date} onChange={(e) => set("invoice_date", e.target.value)} placeholder="DD/MM/YYYY" className="gm-input" data-testid="invoice-date-input" /></Field>
          </div>
          <Field label="Product Details"><Textarea value={form.product_details} onChange={(e) => set("product_details", e.target.value)} placeholder="Items purchased..." rows={3} className="gm-input" /></Field>
          <div className="grid grid-cols-3 gap-2.5">
            <Field label="Subtotal (£)"><Input type="number" step="0.01" value={form.subtotal} onChange={(e) => set("subtotal", e.target.value)} className="gm-input font-mono" data-testid="purchase-subtotal-input" /></Field>
            <Field label="VAT (£)"><Input type="number" step="0.01" value={form.vat_amount} onChange={(e) => set("vat_amount", e.target.value)} className="gm-input font-mono" data-testid="purchase-vat-input" /></Field>
            <Field label="Total (£)"><Input type="number" step="0.01" value={form.total_amount} onChange={(e) => set("total_amount", e.target.value)} className="gm-input font-mono font-bold" data-testid="purchase-amount-input" /></Field>
            {form.total_amount !== "" && Math.abs((parseFloat(form.subtotal) || 0) + (parseFloat(form.vat_amount) || 0) - (parseFloat(form.total_amount) || 0)) > 0.01 && (
              <p className="text-xs text-amber-600 flex items-center gap-1" data-testid="total-mismatch-warning"><AlertTriangle className="w-3 h-3" /> Subtotal + VAT does not equal the total. Please check the invoice.</p>
            )}
          </div>
          <Separator />
          <div className="flex gap-3">
            <button onClick={() => setStep(1)} className="flex-1 gm-btn-navy flex items-center justify-center gap-2" data-testid="step2-back-btn"><ArrowLeft className="w-4 h-4" /> Back</button>
            <button onClick={() => setStep(3)} className="flex-1 gm-btn-gold flex items-center justify-center gap-2" data-testid="step2-next-btn">Next <ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <PaymentChoice form={form} set={set} />
          <label className="flex items-center gap-3 cursor-pointer py-1">
            <input type="checkbox" checked={form.is_credit_note} onChange={(e) => set("is_credit_note", e.target.checked)} className="w-4 h-4 rounded border-navy/20 text-gold focus:ring-gold/30" />
            <span className="text-sm text-navy">This is a credit note</span>
          </label>
          <Field label="Notes"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Additional notes..." rows={2} className="gm-input" /></Field>
          <Card className="border-navy/10 shadow-none">
            <CardContent className="p-4 space-y-2">
              <h3 className="font-semibold text-navy text-sm">Summary</h3>
              <div className="grid grid-cols-2 gap-y-1.5 text-sm">
                <span className="text-navy/50">Supplier</span><span className="text-navy font-medium truncate">{form.supplier_name || suppliers.find((s) => String(s.id) === form.supplier_id)?.name || "\u2014"}</span>
                <span className="text-navy/50">Invoice</span><span className="text-navy">{form.invoice_number || "\u2014"}</span>
                <span className="text-navy/50">Total</span><span className="text-navy font-mono font-bold">{formatGBP(form.total_amount)}</span>
                <span className="text-navy/50">Payment</span><span className="text-navy" data-testid="summary-payment">{phase2 ? (form.payment_status === "paid" ? `Paid in Full · ${(form.payment_method || "").replace("_", " ")}` : "Payment Pending") : form.payment_status}</span>
                <span className="text-navy/50">Documents</span><span className="text-navy">{documents.length} attached</span>
              </div>
            </CardContent>
          </Card>
          <Separator />
          <div className="flex gap-3">
            <button onClick={() => setStep(2)} className="flex-1 gm-btn-navy flex items-center justify-center gap-2 text-sm" data-testid="step3-back-btn"><ArrowLeft className="w-4 h-4" /> Back</button>
          </div>
          <div className="flex flex-col gap-2 sticky bottom-0 z-10 bg-cream -mx-4 px-4 pt-3 pb-3 border-t border-navy/10" data-testid="sticky-actions">
            <ConfirmDetails checked={confirmed} onChange={setConfirmed} kind="invoice" />
            <div className="flex gap-3">
            <button onClick={() => save(false)} disabled={saving} className="flex-1 gm-btn-navy flex items-center justify-center gap-2 text-sm" data-testid="save-draft-btn"><Save className="w-4 h-4" /> Draft</button>
            <button onClick={() => save(true)} disabled={saving || documents.length === 0 || !confirmed} className="flex-1 gm-btn-gold flex items-center justify-center gap-2 text-sm" data-testid="submit-submission-button"><Send className="w-4 h-4" /> Submit</button>
            </div>
          </div>
          {documents.length === 0 && (
            <p className="text-xs text-amber-600 text-center flex items-center justify-center gap-1"><AlertTriangle className="w-3 h-3" /> Upload a document to submit</p>
          )}
        </div>
      )}
      <DuplicateDialog conflict={conflict} busy={saving} onClose={() => setConflict(null)}
        onRequestException={(reason) => save(false, { ...conflict.extra, duplicate_review_reason: reason })}
        onContinue={() => save(conflict.submit, { ...conflict.extra, acknowledge_cross_store: true })} />
    </div>
  );
}

function Field({ label, children }) {
  return (<div className="space-y-1.5"><Label className="text-sm font-medium text-navy">{label}</Label>{children}</div>);
}

function StepIndicator({ steps, current }) {
  return (
    <div className="flex items-center gap-1.5">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center gap-1.5 flex-1">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i + 1 < current ? "gm-step-completed" : i + 1 === current ? "gm-step-active" : "gm-step-pending"}`}>
            {i + 1 < current ? <Check className="w-3.5 h-3.5" /> : i + 1}
          </div>
          <span className={`text-[11px] hidden sm:inline ${i + 1 === current ? "text-navy font-medium" : "text-navy/30"}`}>{s}</span>
          {i < steps.length - 1 && <div className="flex-1 h-px bg-navy/10" />}
        </div>
      ))}
    </div>
  );
}
